import React from 'react';
import PropTypes from 'prop-types';

import get from 'lodash/get';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';

import './styles.css';
import messages from './messages';
import ApplicationDeadlines from './ApplicationDeadlines';
import { formatPhoneNumber, getApplicationDeadlines } from '../utils';

function getPhoneNumber(transferAdmissions, admissionsContact) {
  const phone = get(transferAdmissions, 'contactRefPhone.value');
  if (phone) {
    return phone;
  }
  return get(admissionsContact, 'phone', '');
}

function getEmailAddress(transferAdmissions, admissionsContact) {
  const email = get(transferAdmissions, 'contactRefEmail.value');
  if (email) {
    return email;
  }
  return get(admissionsContact, 'email', '');
}

function isAppMember(commonApp, coalitionApp) {
  if (commonApp || coalitionApp) {
    return true;
  }
  return false;
}

function SubSectionWrapper({ title, content, cypressKey, className }) {
  return (
    <div className="subsection" data-cy={cypressKey}>
      <p className="margin-bottom-half subsection-title">{title}</p>
      <div
        className={classNames({ 'subsection-content': true, [className]: !!className })}
      >
        {content}
      </div>
    </div>
  );
}

function AdmissionsInformation({
  intl,
  importantPolicies,
  admissionsContact,
  addToTransferPlan,
  transferAdmissions,
  admissionsContactInfoRef,
}) {
  const deadlines = getApplicationDeadlines(get(transferAdmissions, 'deadlines', []));
  const minHomeCredits = get(transferAdmissions, 'minHomeCredits.value', '');
  const commonAppMember = get(importantPolicies, 'commonAppMember.value', false);
  const coalitionAppMember = get(importantPolicies, 'coalitionAppMember.value', false);
  const admissionsRequirements = get(
    transferAdmissions,
    'admissionsRequirements.value',
    [],
  );
  const appMember = isAppMember(commonAppMember, coalitionAppMember);
  const phone = getPhoneNumber(transferAdmissions, admissionsContact);
  const email = getEmailAddress(transferAdmissions, admissionsContact);

  const formattedPhone = React.useMemo(() => {
    return formatPhoneNumber(phone);
  }, [phone]);

  return (
    <div
      data-cy="admissions-information"
      className="transfer-common-container common-core-width"
    >
      <p className="section-title margin-bottom-half">
        {intl.formatMessage(messages.admissionsInformation)}
      </p>
      {minHomeCredits || admissionsRequirements.length || appMember ? (
        <SubSectionWrapper
          cypressKey="application-requirements"
          title={intl.formatMessage(messages.transferApplicationsRequirements)}
          content={
            <div>
              {minHomeCredits && (
                <p className="section-text margin-bottom-half">
                  <FormattedMessage
                    id={messages.minHomeCredits.id}
                    defaultMessage={messages.minHomeCredits.defaultMessage}
                    values={{
                      credits: minHomeCredits,
                    }}
                  />
                </p>
              )}
              <div className="section-text admissions-requirements">
                {admissionsRequirements.length ? (
                  <div>
                    <p className="margin-0">{intl.formatMessage(messages.required)}:</p>
                    <ul>
                      {admissionsRequirements.map((item, idx) => (
                        <li key={`requirement-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {appMember && (
                  <div>
                    <p className="margin-0">
                      {intl.formatMessage(messages.applicationSubmissionOptions)}:
                    </p>
                    <ul>
                      {commonAppMember && <li>Common App</li>}
                      {coalitionAppMember && <li>Coalition App</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          }
        />
      ) : null}
      {deadlines.length ? (
        <SubSectionWrapper
          className="application-deadlines"
          cypressKey="application-deadlines"
          title={intl.formatMessage(messages.applicationDeadlines)}
          content={
            <ApplicationDeadlines
              deadlines={deadlines}
              addToTransferPlan={addToTransferPlan}
            />
          }
        />
      ) : null}
      <SubSectionWrapper
        cypressKey="contact-information"
        title={intl.formatMessage(messages.contactInformation)}
        content={
          <div
            ref={admissionsContactInfoRef}
            className="section-text admissions-requirements admissions-contact"
          >
            <div>
              <p className="margin-0">
                {intl.formatMessage(messages.phone)}: <span>{formattedPhone}</span>
              </p>
            </div>
            <div>
              <p className="margin-0">
                {intl.formatMessage(messages.email)}: <span>{email}</span>
              </p>
            </div>
          </div>
        }
      />
    </div>
  );
}

AdmissionsInformation.propTypes = {
  intl: PropTypes.object,
  transferAdmissions: PropTypes.object,
};

export default injectIntl(AdmissionsInformation);
